// Carrito de la promo (misión 07d-carrito): el comportamiento propio del carrito, sobre el carrito nativo de Horizon.
// No abre ni cierra el drawer ni pinta líneas: cambia el carrito con la Ajax API y despacha CartLinesUpdateEvent con
// las secciones ya renderizadas, igual que product-form.js, para que component-cart-items.js haga el morph y
// cart-drawer.js decida si abre. Un solo controlador del drawer.
//
// - <skindion-cart> (snippets/skindion-cart-lines.liquid) trae el estado del carrito en data-state. Cada vez que
//   cambia, revisa la regla (assets/skindion-cart-rules.js) y corrige: agrega el gel del plan y los 4 regalos con el
//   Skindion (un juego por orden) y los quita sin Skindion, también si llegaron por URL.
// - «Quitar» del Skindion quita su línea y, si era el último, el juego completo en la misma petición.
// - Switch de Priority+ (la píldora de garantía cambia al instante) y «Completa tu ritual» (compra única).
// - Pop-up de Priority+ al dar clic en «Finalizar pedido», una vez por sesión y solo sin Priority+ en el carrito.
// - addPromoDevice(): lo usa el botón de la oferta (assets/skindion-offer.js) para agregar el Skindion con su juego.
// - addCaja() (07r): lo usa el botón de la Caja Ritual (assets/skindion-geles-v2.js). La sincronización también corrige
//   la caja (cascada sin edición parcial, assets/skindion-cart-rules.js: cajaCartSync).
// La configuración (Skindion, gel del plan y su plan de venta, regalos) sale del Liquid: #skd-cart-config en
// layout/theme.liquid.
import { CartLinesUpdateEvent } from '@shopify/events';
import { CartSectionRestoredEvent } from '@theme/events';
import { CartItemsComponent } from '@theme/component-cart-items';
import { cajaItems, cajaReplaceUpdates, cartSync, hasChanges, hasTwoForOne, linesFromCart, priorityFor, priorityOffUpdates, promoAddItems, promoSwapBeforeAdd, removeDeviceUpdates } from '@theme/skindion-cart-rules';

export const NETWORK_ERROR = 'No pudimos actualizar tu carrito. Revisa tu conexión e intenta de nuevo.';

/** @returns {import('./skindion-cart-rules.js').CartConfig} */
export function cartConfig() {
  try {
    return { devices: [], limit: 0, companions: [], ...JSON.parse(document.getElementById('skd-cart-config')?.textContent || '{}') };
  } catch {
    return { devices: [], limit: 0, companions: [] };
  }
}

const sectionIds = () =>
  [...document.querySelectorAll('cart-items-component')].map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : '')).filter(Boolean);

/**
 * `silent`: Shopify publica un product_added_to_cart por cada línea de /cart/add (shop_events_listener lee
 * arguments[1].body) y de ahí salen el AddToCart de Meta y el add_to_cart de GA4. El gel del plan y los regalos no son
 * una decisión de compra: con un solo Request como argumento el listener no los cuenta. Si Shopify cambia el listener,
 * lo peor que pasa es que vuelvan los eventos de más; el carrito funciona igual.
 *
 * `realValue`: el AddToCart de esta alta sale con el precio final de la línea, no con el de catálogo
 * (snippets/skindion-atc-real-value.liquid). Para entonces el descuento ya debe aplicar: los acompañantes van antes.
 */
async function cartPost(url, body, { silent = false, realValue = false } = {}) {
  const init = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: sectionIds().join(','), sections_url: window.location.pathname }),
    ...(realValue ? { skdRealValue: true } : {}),
  };
  const response = await (silent ? fetch(new Request(url, init)) : fetch(url, init));
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json.status) throw Object.assign(new Error(json.description || json.message || 'Cart request failed'), { response: json });
  return json;
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * Cambia el carrito (primero /cart/update.js, luego los acompañantes y al final lo que la clienta eligió) y avisa al
 * theme con un solo evento. Horizon hace el morph con las secciones de la última respuesta. `action` sigue a
 * CartLinesUpdateEvent: con 'add' el drawer se abre (cart-drawer.js, auto-open); la sincronización usa 'update' para no
 * abrirlo sola. `add` cuenta como AddToCart; `silentAdd` no (ver cartPost).
 * @param {{ updates?: Record<string, number>, add?: { id: number, quantity: number, selling_plan?: number }[], silentAdd?: { id: number, quantity: number, selling_plan?: number }[], action: 'add' | 'update' | 'remove', source: Element }} change
 */
export async function mutateCart({ updates = {}, add = [], silentAdd = [], action, source }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const lines = [
    ...Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
    ...[...silentAdd, ...add].map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
  ];
  source.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
  try {
    let sections;
    if (Object.keys(updates).length) ({ sections } = await cartPost(Theme.routes.cart_update_url, { updates }));
    if (silentAdd.length) ({ sections } = await cartPost(Theme.routes.cart_add_url, { items: silentAdd }, { silent: true }));
    if (add.length) ({ sections } = await cartPost(Theme.routes.cart_add_url, { items: add }, { realValue: true }));
    const cart = await fetchCart();
    // El carrito ya cambió: si la conversión para el evento falla, no se reporta como error ni se corta el flujo.
    let eventCart;
    try {
      eventCart = CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
    } catch (error) {
      console.warn('[skindion-cart] Evento sin carrito:', error);
    }
    deferred.resolve({
      cart: eventCart,
      detail: { items: cart.items, itemCount: cart.item_count, sections, source: 'skindion-cart', didError: false },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/** Estado actual del carrito: el que pintó el Liquid o, si el carrito no está en la página, /cart.js. */
async function currentLines() {
  const state = document.querySelector('skindion-cart');
  if (state instanceof SkindionCart) return state.lines;
  return linesFromCart(await fetchCart());
}

/**
 * Botón de la oferta: agrega el juego (si es el primer Skindion) sin contarlo como AddToCart y al final el Skindion, que
 * es el único evento del clic: si el Skindion no entra, no se reporta. Con el 2º Skindion, el dummy de 2 entra antes: el
 * descuento ya aplica cuando sale el evento, que así lleva el valor real. Devuelve el carrito.
 * @param {number} variantId @param {Element} source
 */
export async function addPromoDevice(variantId, source) {
  const lines = await currentLines();
  const config = cartConfig();
  const [device, ...companions] = promoAddItems(variantId, lines, config);
  const incoming = config.twoForOne?.variants?.includes(variantId) ? config.twoForOne.handles[0] : undefined;
  const swap = promoSwapBeforeAdd(lines, config, incoming);
  return mutateCart({ updates: swap.updates, silentAdd: [...swap.add, ...companions], add: [device], action: 'add', source });
}

/**
 * 07r · Botón de la Caja Ritual: quita la caja anterior (un segundo «Agregar» la reemplaza) y agrega la nueva. Los geles
 * son la decisión de compra y cuentan como AddToCart (al final, con el valor real: el dummy ya entró); el dummy del plan
 * y los regalos van en silencio. Después guarda los atributos de la landing (_lnd y _oferta) como la promo: /cart/update.js
 * solo con attributes suma esas claves a las que ya hay (ref y atribución se quedan). Devuelve el carrito.
 * @param {string} key - clave de la caja («2H+1R») @param {import('./skindion-cart-rules.js').CajaConfig} caja @param {Element} source
 */
export async function addCaja(key, caja, source) {
  const items = cajaItems(key, caja);
  if (!items.length) throw new Error(`Caja fuera del contrato: ${key}`);
  const updates = cajaReplaceUpdates(linesFromCart(await fetchCart()), caja);
  const gels = items.filter((item) => item.properties._skd_caja && item.selling_plan == null);
  const companions = items.filter((item) => !gels.includes(item));
  const cart = await mutateCart({ updates, silentAdd: companions, add: gels, action: 'add', source });
  const attributes = Object.fromEntries(Object.entries(caja.attributes ?? {}).filter(([, value]) => value));
  if (Object.keys(attributes).length) {
    try {
      await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
    } catch {
      // Sin red: la medición no frena la compra.
    }
  }
  return cart;
}

/* ---------- Sincronización ---------- */

let syncing = false;
let lastChange = '';
let repeats = 0;

class SkindionCart extends HTMLElement {
  static observedAttributes = ['data-state'];
  #timer = 0;

  /** @returns {import('./skindion-cart-rules.js').CartLine[]} */
  get lines() {
    try {
      return JSON.parse(this.dataset.state || '[]');
    } catch {
      return [];
    }
  }

  connectedCallback() {
    this.#remeasure();
    this.#schedule();
  }

  attributeChangedCallback() {
    this.#remeasure();
    this.#schedule();
  }

  /**
   * El carrito se acaba de pintar (morph): cart-drawer.js vuelve a medir el pie fijo. Al llenar el drawer vacío medía
   * antes del morph, sin resumen, y dejaba cart-summary-sticky="false" (el pie salía de la pantalla, render real).
   * Es el mismo evento con el que Horizon vuelve a medir tras restaurar la página; no abre ni cierra nada.
   */
  #remeasure() {
    requestAnimationFrame(() => this.isConnected && this.dispatchEvent(new CartSectionRestoredEvent()));
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  #schedule() {
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#sync(), 150);
  }

  async #sync() {
    if (!this.isConnected || syncing) return;
    // Primero terminan los cambios de cantidad que Horizon tenga en vuelo.
    await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
    // La regla de la promo y la de la Caja Ritual (07r) en un solo cambio.
    const change = cartSync(this.lines, cartConfig());
    if (!hasChanges(change)) {
      lastChange = '';
      repeats = 0;
      return;
    }
    // Si Shopify no deja el cambio (sin existencias, producto despublicado), no se insiste en bucle.
    const signature = JSON.stringify(change);
    repeats = signature === lastChange ? repeats + 1 : 0;
    lastChange = signature;
    if (repeats > 1) return;

    syncing = true;
    this.setAttribute('aria-busy', 'true');
    try {
      await mutateCart({ updates: change.updates, silentAdd: change.add, action: 'update', source: this });
    } catch (error) {
      console.warn('[skindion-cart] No se pudo ajustar el carrito:', error);
    } finally {
      syncing = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('skindion-cart')) customElements.define('skindion-cart', SkindionCart);

/* ---------- Controles del carrito (delegados: el morph reemplaza los nodos) ---------- */

/** Tras un morph el foco puede quedar en <body>: vuelve a un control vivo del carrito. */
function restoreFocus(scope) {
  window.setTimeout(() => {
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return;
    const target = [
      scope?.querySelector('.skd-cart-line__remove'),
      scope?.querySelector('.cart-items__empty-button'),
      scope?.closest('dialog')?.querySelector('.theme-drawer__close-button'),
      document.querySelector('#cart-drawer .theme-drawer__close-button'),
    ].find((el) => el instanceof HTMLElement && el.isConnected);
    target?.focus({ preventScroll: true });
  }, 60);
}

function showLineError(row, message) {
  const container = row?.querySelector('.skd-cart-line__error');
  const text = container?.querySelector('.cart-item__error-text');
  if (!container || !text) return;
  text.textContent = message;
  container.classList.remove('hidden');
}

/* ---------- Pop-up de Priority+ al dar clic en «Finalizar pedido» (snippets/skindion-priority-popup.liquid) ----------
   Solo si Priority+ no está ya en el carrito y una vez por sesión (sessionStorage, con espejo en memoria si el
   navegador la bloquea). Nunca al agregar al carrito. */

const OFFERED_KEY = 'skdPpOffered';
let offeredMemory = false;
let popupTrigger = null;

function wasOffered() {
  if (offeredMemory) return true;
  try {
    return window.sessionStorage.getItem(OFFERED_KEY) === '1';
  } catch {
    return false;
  }
}

function markOffered() {
  offeredMemory = true;
  try {
    window.sessionStorage.setItem(OFFERED_KEY, '1');
  } catch {
    // Sin sessionStorage basta la memoria de esta página.
  }
}

/** Sigue al checkout con el formulario del carrito (name=checkout), como el botón. */
function proceedToCheckout() {
  const form = document.getElementById('cart-form');
  const button = document.querySelector('[data-skd-checkout]');
  if (button instanceof HTMLButtonElement && button.disabled) return;
  if (form instanceof HTMLFormElement && button instanceof HTMLButtonElement) form.requestSubmit(button);
  else window.location.assign(`${window.Shopify?.routes?.root ?? '/'}checkout`);
}

/** @param {HTMLDialogElement} popup @param {boolean} returnFocus - X, fuera o Esc: regresa al carrito con el foco en el botón. */
function closePopup(popup, returnFocus) {
  if (popup.open) popup.close();
  if (returnFocus && popupTrigger instanceof HTMLElement && popupTrigger.isConnected) popupTrigger.focus();
  else if (returnFocus) document.querySelector('[data-skd-checkout]')?.focus();
  popupTrigger = null;
}

/**
 * 07f: el pop-up se pinta una vez con la página y el carrito puede cambiar de oferta después. Al abrir, pone el precio
 * del Priority+ que toca al carrito actual (priorityFor): en uno del 2x1, $190 solo en el botón; en el de la promo, $90 con
 * $190 tachado. Los precios van en el pop-up por variante ([data-skd-pp-prices]).
 * @param {HTMLDialogElement} popup
 */
function syncPopupPrice(popup) {
  const state = document.querySelector('skindion-cart');
  if (!(state instanceof SkindionCart)) return;
  // En un carrito del 2x1 el precio va solo en el botón (pedido de Mau); en el de la promo, también arriba con su tachado.
  const line = popup.querySelector('[data-skd-pp-price-line]');
  if (line instanceof HTMLElement) line.hidden = hasTwoForOne(state.lines, cartConfig());
  const variant = priorityFor(state.lines, cartConfig())?.variant;
  const data = variant ? popup.querySelector(`[data-skd-pp-prices] [data-variant="${variant}"]`) : null;
  const price = data?.getAttribute('data-price');
  if (!price) return;
  const compare = data.getAttribute('data-compare') || '';
  const final = popup.querySelector('.skd-pp__final');
  if (final) final.textContent = price;
  const wrap = popup.querySelector('[data-skd-pp-compare-wrap]');
  if (wrap instanceof HTMLElement) wrap.hidden = !compare;
  const was = popup.querySelector('.skd-pp__compare');
  if (was && compare) was.textContent = compare;
  const label = popup.querySelector('[data-skd-pp-accept-label]');
  const text = popup.querySelector('[data-skd-pp-accept]')?.getAttribute('data-skd-pp-accept-text');
  if (label && text) label.textContent = `${text} · ${price}`;
}

/** @param {HTMLDialogElement} popup */
function openPopup(popup, trigger) {
  popupTrigger = trigger;
  syncPopupPrice(popup);
  const error = popup.querySelector('[data-skd-pp-error]');
  if (error) error.textContent = '';
  popup.showModal();
  popup.querySelector('[data-skd-pp-close]')?.focus();
}

const FOCUSABLE = 'button:not([disabled]), a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

function wirePopup(popup) {
  if (!(popup instanceof HTMLDialogElement) || popup.dataset.skdPpWired) return;
  popup.dataset.skdPpWired = '1';

  popup.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      // Antes que el drawer (theme-drawer.js respeta defaultPrevented): solo se cierra el pop-up.
      event.preventDefault();
      event.stopPropagation();
      closePopup(popup, true);
    } else if (event.key === 'Tab') {
      const items = [...popup.querySelectorAll(FOCUSABLE)].filter((el) => el instanceof HTMLElement && el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !popup.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !popup.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  // Por si el navegador dispara el cancel nativo del <dialog>: mismo cierre, de vuelta al carrito.
  popup.addEventListener('cancel', (event) => {
    event.preventDefault();
    closePopup(popup, true);
  });

  // Clic fuera de la tarjeta (en el fondo del <dialog>) = cerrar.
  popup.addEventListener('click', (event) => {
    if (event.target === popup) closePopup(popup, true);
  });
  popup.querySelector('[data-skd-pp-close]')?.addEventListener('click', () => closePopup(popup, true));

  popup.querySelector('[data-skd-pp-decline]')?.addEventListener('click', () => {
    closePopup(popup, false);
    proceedToCheckout();
  });

  const accept = popup.querySelector('[data-skd-pp-accept]');
  const label = popup.querySelector('[data-skd-pp-accept-label]');
  accept?.addEventListener('click', async () => {
    if (!(accept instanceof HTMLButtonElement) || accept.getAttribute('aria-busy') === 'true') return;
    const variant = priorityFor(await currentLines(), cartConfig())?.variant;
    const error = popup.querySelector('[data-skd-pp-error]');
    const original = label?.textContent ?? '';
    accept.setAttribute('aria-busy', 'true');
    if (label && accept.dataset.labelLoading) label.textContent = accept.dataset.labelLoading;
    try {
      if (variant) await mutateCart({ add: [{ id: variant, quantity: 1 }], action: 'update', source: accept });
      closePopup(popup, false);
      proceedToCheckout();
    } catch (e) {
      if (error) error.textContent = e?.response?.description || e?.response?.message || NETWORK_ERROR;
    } finally {
      accept.removeAttribute('aria-busy');
      if (label) label.textContent = original;
    }
  });
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const checkout = target?.closest('[data-skd-checkout]');
    if (!(checkout instanceof HTMLButtonElement) || checkout.disabled) return;
    const popup = document.getElementById('SkdPriorityPopup');
    const priorityInCart =
      checkout.closest('[data-skd-cart-footer]')?.querySelector('[data-skd-priority-switch]')?.getAttribute('aria-checked') === 'true';
    if (!(popup instanceof HTMLDialogElement) || priorityInCart || wasOffered() || !cartConfig().priority?.variant) return;
    event.preventDefault();
    event.stopPropagation();
    wirePopup(popup);
    markOffered();
    openPopup(popup, checkout);
  },
  true
);

/* Switch de Priority+: la píldora cambia en el acto (data-skd-pill) y el carrito se actualiza detrás. */
function setPriority(footer, on) {
  const toggle = footer?.querySelector('[data-skd-priority-switch]');
  toggle?.setAttribute('aria-checked', String(on));
  if (footer) footer.dataset.skdPill = on ? '100' : footer.dataset.skdPillBase || 'none';
}

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const toggle = target?.closest('[data-skd-priority-switch]');
  if (!(toggle instanceof HTMLButtonElement) || toggle.getAttribute('aria-busy') === 'true') return;
  event.preventDefault();

  const footer = toggle.closest('[data-skd-cart-footer]');
  const wasOn = toggle.getAttribute('aria-checked') === 'true';
  const config = cartConfig();
  setPriority(footer, !wasOn);
  toggle.setAttribute('aria-busy', 'true');
  try {
    if (wasOn) {
      await mutateCart({ updates: priorityOffUpdates(await currentLines(), config), action: 'update', source: toggle });
    } else {
      // 07f: el que toca al carrito (en uno del 2x1, Priority Plus $190).
      const variant = priorityFor(await currentLines(), config)?.variant;
      if (variant) await mutateCart({ add: [{ id: variant, quantity: 1 }], action: 'update', source: toggle });
    }
  } catch {
    setPriority(footer, wasOn);
  } finally {
    if (toggle.isConnected) toggle.removeAttribute('aria-busy');
  }
});

/* «Completa tu ritual»: compra única (sin plan). La tarjeta desaparece cuando el producto ya está en el carrito. */
document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const add = target?.closest('[data-skd-ritual-add]');
  if (!(add instanceof HTMLButtonElement) || add.getAttribute('aria-busy') === 'true') return;
  event.preventDefault();

  const error = add.closest('.skd-ritual__card')?.querySelector('[data-skd-ritual-error]');
  if (error) error.textContent = '';
  add.setAttribute('aria-busy', 'true');
  try {
    await mutateCart({ add: [{ id: Number(add.dataset.skdRitualAdd), quantity: 1 }], action: 'add', source: add });
  } catch (e) {
    if (error) error.textContent = e?.response?.description || e?.response?.message || NETWORK_ERROR;
  } finally {
    if (add.isConnected) add.removeAttribute('aria-busy');
  }
});

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const remove = target?.closest('[data-skd-remove-device]');
  if (!(remove instanceof HTMLButtonElement) || remove.disabled) return;
  event.preventDefault();

  const scope = remove.closest('cart-items-component');
  const row = remove.closest('.skd-cart-line');
  const updates = removeDeviceUpdates(await currentLines(), cartConfig(), remove.dataset.skdRemoveDevice ?? '');
  if (!Object.keys(updates).length) return;
  remove.disabled = true;
  row?.setAttribute('aria-busy', 'true');
  try {
    await mutateCart({ updates, action: 'remove', source: remove });
    restoreFocus(scope);
  } catch {
    remove.disabled = false;
    row?.removeAttribute('aria-busy');
    showLineError(row, NETWORK_ERROR);
  }
});
